"use client";

import { Loader2, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

const DEBOUNCE_MS = 250;

/**
 * Production search input — "lifting card" treatment.
 *
 * Visual contract:
 *   - Soft rounded card (border + tag-bg fill) at rest.
 *   - On focus: lifts −2px, gains a soft shadow, lupa nudges right 2px.
 *   - On hover (no focus): light hint shadow + border tint, subtle invite.
 *
 * Behavior contract (covers the search-UX playbook edge cases):
 *   - Controlled value with internal debounced draft (250ms).
 *   - Empty / focused / typing / loading / with-value states never overlap;
 *     the input is never disabled — even while loading the user can keep
 *     typing, and the previous query stays editable.
 *   - Native `<input type="search">` browser clear button is hidden globally
 *     in `global.css`; we render our own X.
 *   - Loading: spinner replaces the clear button (so we never show two
 *     trailing affordances at once); when loading ends mid-typing, the X
 *     comes back without flicker.
 *   - Hotkey: pressing `/` anywhere on the page (outside other inputs)
 *     focuses this field. A small `/` keycap hint sits on the right when
 *     the field is empty + unfocused; it fades on focus or first keystroke.
 *   - Escape clears the field and blurs.
 *   - Enter commits the current draft immediately (skips the pending
 *     debounce) so power users don't wait the extra 250ms.
 *   - aria-label always reads the placeholder so screen readers know the
 *     scope; `role="search"` lives on the parent landmark in the page.
 */
export function ExploreSearchInput({
  value,
  onDebouncedChange,
  placeholder = "Search stories and briefs",
  className,
  isLoading = false,
}: {
  value: string;
  onDebouncedChange: (next: string) => void;
  placeholder?: string;
  className?: string;
  isLoading?: boolean;
}) {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  const lastEmittedRef = useRef(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync local draft when the parent resets `value` programmatically
  // (e.g., a "Clear all" reset action).
  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      lastEmittedRef.current = value;
      setDraft(value);
    }
  }, [value]);

  // Debounce.
  useEffect(() => {
    if (draft === lastEmittedRef.current) {
      return;
    }
    const handle = setTimeout(() => {
      lastEmittedRef.current = draft;
      onDebouncedChange(draft);
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [draft, onDebouncedChange]);

  // `/` hotkey — focus the search input from anywhere on the page.
  // Skip if the user is already typing in another input/textarea/contenteditable.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) {
        return;
      }
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        target?.isContentEditable === true
      ) {
        return;
      }
      e.preventDefault();
      inputRef.current?.focus();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const handleClear = () => {
    setDraft("");
    lastEmittedRef.current = "";
    onDebouncedChange("");
    inputRef.current?.focus();
  };

  const showSpinner = isLoading && draft.length > 0;
  const showClear = draft.length > 0 && !showSpinner;

  return (
    <div
      className={cn(
        "relative flex h-16 w-full items-center gap-4 rounded-2xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)] pr-2 pl-6 transition-all duration-300 ease-out",
        focused
          ? "-translate-y-0.5 border-[var(--explore-text-primary)]/15 shadow-[0_10px_28px_-14px_rgba(0,0,0,0.22)]"
          : "translate-y-0 hover:shadow-[0_4px_14px_-10px_rgba(0,0,0,0.16)]",
        className
      )}
    >
      <Search
        aria-hidden
        className={cn(
          "h-5 w-5 shrink-0 transition-all duration-300 ease-out",
          focused
            ? "translate-x-0.5 text-[var(--explore-text-primary)]"
            : "text-[var(--explore-text-muted)]"
        )}
      />
      <input
        aria-label={placeholder}
        autoComplete="off"
        className="min-w-0 flex-1 bg-transparent text-[1.125rem] text-[var(--explore-text-primary)] placeholder:text-[var(--explore-text-muted)] focus:outline-none"
        onBlur={() => setFocused(false)}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={() => setFocused(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            // Commit immediately — skip the pending debounce.
            lastEmittedRef.current = draft;
            onDebouncedChange(draft);
            e.currentTarget.blur();
          }
          if (e.key === "Escape" && draft.length > 0) {
            handleClear();
            e.currentTarget.blur();
          }
        }}
        placeholder={placeholder}
        ref={inputRef}
        spellCheck={false}
        type="search"
        value={draft}
      />
      {showSpinner && (
        <Loader2
          aria-hidden
          aria-label="Searching"
          className="h-5 w-5 shrink-0 animate-spin text-[var(--explore-text-muted)]"
        />
      )}
      {showClear && (
        <button
          aria-label="Clear search"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[var(--explore-text-muted)] transition-all duration-150 hover:bg-[var(--explore-border-light)] hover:text-[var(--explore-text-primary)]"
          onClick={handleClear}
          type="button"
        >
          <X aria-hidden className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
